import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect, useContext } from 'react';
import "leaflet/dist/leaflet.css";
import { AuthProvider, AuthContext } from './context/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import Profile from './components/Profile';
import About from './components/About';
import Contact from './components/Contact';
import MainMenu from './components/MainMenu';
import AdminMenu from './components/AdminMenu';
import ProfileMenu from './components/ProfileMenu';
import Preferences from './components/Preferences';
import Favorites from './components/Favorites';
import MyDiscounts from './components/MyDiscounts';
import ShowOnMap from './components/ShowOnMap';
import AllDiscounts from './components/Discounts';
import ViewDiscount from './components/ViewDiscount';
import AddDiscount from './components/AddDiscount';
import EditDiscount from './components/EditDiscount';
import AddStore from './components/AddStore';
import AdminDiscounts from './components/AdminDiscounts';
import AdminStores from './components/AdminStores';
import AdminEditStore from './components/AdminEditStore';
import AdminCategories from './components/AdminCategories';
import AdminDashboard from './components/AdminDashboard';
import AdminUsers from './components/AdminUsers';
import OfflineBanner from './components/OfflineBanner';
import { getPendingActions, deletePendingAction, saveDiscounts, saveFavorites, saveFormData } from './utils/offlineDB';

// Decode the userId from a JWT token payload without making a network request.
// The payload is the middle segment of the token, base64url encoded.
const getUserIdFromToken = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).userId;
  } catch {
    return null;
  }
};

// AppContent sits inside AuthProvider so it can access the current accessToken.
// It owns the Router and replays any actions that were queued while offline.
const AppContent = () => {
  const { accessToken, language, user } = useContext(AuthContext);

  // -----------------------------------------------------------------------
  // Background pre-fetch: silently cache key data when the user is online
  // and logged in. This ensures pages work offline even if never visited.
  // Runs 4 seconds after login so it doesn't compete with the page load.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!accessToken || !navigator.onLine) return;

    const lang = language || 'el';
    const headers = { Authorization: `Bearer ${accessToken}` };
    const base = process.env.REACT_APP_BACKEND_URL;

    const prefetch = async () => {
      // Pre-fetch first page of discounts
      try {
        const res = await fetch(
          `${base}/discounts/alldiscounts?lang=${lang}&sortBy=created_at&order=desc&limit=20&offset=0`,
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.discounts?.length) saveDiscounts(data.discounts).catch(() => {});
        }
      } catch { /* silent — just an optimisation */ }

      // Pre-fetch favorites
      try {
        const res = await fetch(
          `${base}/favorites/myfavorites?lang=${lang}&sortBy=created_at&order=desc&limit=20&offset=0`,
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.favorites?.length) saveFavorites(data.favorites).catch(() => {});
        }
      } catch { /* silent */ }

      // Pre-fetch form dropdown data for AddDiscount (both languages)
      // This ensures categories, cities, areas and stores are available offline
      // even if the user has never visited the Add Discount page
      for (const l of ['el', 'en']) {
        try {
          const res = await fetch(`${base}/adddiscount/prefetch?lang=${l}`, { headers });
          if (res.ok) {
            const data = await res.json();
            saveFormData(`dropdown_${l}`, data).catch(() => {});
          }
        } catch { /* silent */ }
      }
    };

    const timer = setTimeout(prefetch, 4000);
    return () => clearTimeout(timer);
  }, [accessToken, language]);

  useEffect(() => {
    if (!accessToken) return;

    const replayPendingActions = async () => {
      // Decode userId directly from the token — no extra network request needed
      const currentUserId = getUserIdFromToken(accessToken);
      const actions = await getPendingActions();
      for (const action of actions) {
        // Skip actions that belong to a different user account
        if (action.userId && currentUserId && action.userId !== currentUserId) continue;
        try {
          const headers = { Authorization: `Bearer ${accessToken}` };
          let body;

          if (action.type === 'ADD_DISCOUNT') {
            const fd = new FormData();
            Object.entries(action.fields).forEach(([k, v]) => fd.append(k, v));
            if (action.photoBlob) {
              fd.append('photo', new File([action.photoBlob], action.photoName, { type: action.photoType }));
            }
            body = fd;
          } else {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(action.body);
          }

          const res = await fetch(action.url, { method: action.method, headers, body });
          if (res.ok) {
            await deletePendingAction(action.id);
          }
        } catch {
          // Still offline or server error — leave in queue for next attempt
        }
      }
    };

    // Attempt immediately in case we're already online when accessToken becomes available
    if (navigator.onLine) {
      replayPendingActions();
    }

    window.addEventListener('online', replayPendingActions);
    return () => window.removeEventListener('online', replayPendingActions);
  }, [accessToken]); // accessToken alone is enough — no need to wait for user object

  return (
    <>
      <OfflineBanner />
      <Router>
      <Routes>
        <Route path="/" element={<Login />} /> {/* Default page: Landing + Login */}
        <Route path="/register" element={<Register />} /> {/* Register page */}
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/customermenu" element={<ProtectedRoute><MainMenu /></ProtectedRoute>}/>
        <Route path="/adminmenu" element={<ProtectedRoute><AdminMenu /></ProtectedRoute>}/>
        <Route path="/profilemenu" element={<ProtectedRoute><ProfileMenu /></ProtectedRoute>}/>
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>}/>
        <Route path="/preferences" element={<ProtectedRoute><Preferences /></ProtectedRoute>}/>
        <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>}/>
        <Route path="/mydiscounts" element={<ProtectedRoute><MyDiscounts /></ProtectedRoute>}/>
        <Route path="/showonmap" element={<ProtectedRoute><ShowOnMap /></ProtectedRoute>}/>
        <Route path="/discounts" element={<ProtectedRoute><AllDiscounts /></ProtectedRoute>}/>
        <Route path="/viewdiscount" element={<ProtectedRoute><ViewDiscount /></ProtectedRoute>}/>
        <Route path="/adddiscount" element={<ProtectedRoute><AddDiscount /></ProtectedRoute>}/>
        <Route path="/editdiscount/:id" element={<ProtectedRoute><EditDiscount /></ProtectedRoute>} />
        <Route path="/addstore" element={<ProtectedRoute><AddStore /></ProtectedRoute>}/>
        <Route path="/admindiscounts" element={<ProtectedRoute><AdminDiscounts /></ProtectedRoute>}/>
        <Route path="/adminstores" element={<ProtectedRoute><AdminStores /></ProtectedRoute>}/>
        <Route path="/admineditstore/:id" element={<ProtectedRoute><AdminEditStore /></ProtectedRoute>}/>
        <Route path="/admincategories" element={<ProtectedRoute><AdminCategories /></ProtectedRoute>}/>
        <Route path="/admindashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>}/>
        <Route path="/adminusers" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>}/>
      </Routes>
    </Router>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
